import type { InputManager } from '../core/input';
import { h } from './dom';
import { characterDef } from '../content/characters';

/**
 * Virtual touch controls: a floating left-side movement stick, an ability button
 * (bottom-right), and a small pause
 * button (top-right). Only mounted when a touch-capable device is detected.
 * Feeds `InputManager.setVirtual()` every animation frame while a finger is
 * on the stick; shown only during the 'run' screen (hidden over any overlay).
 */
export function isTouchDevice(): boolean {
  return ('ontouchstart' in window) || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0);
}

const STICK_RADIUS = 60;
const DEAD_ZONE = 0.15;

export interface TouchStickVector {
  dx: number;
  dy: number;
  mx: number;
  my: number;
}

/** Clamp a touch offset to the stick and remap its dead zone without a speed jump. */
export function resolveTouchStick(dx: number, dy: number): TouchStickVector {
  const rawLength = Math.hypot(dx, dy);
  if (rawLength > STICK_RADIUS) {
    dx = (dx / rawLength) * STICK_RADIUS;
    dy = (dy / rawLength) * STICK_RADIUS;
  }
  const nx = dx / STICK_RADIUS;
  const ny = dy / STICK_RADIUS;
  const magnitude = Math.hypot(nx, ny);
  if (magnitude <= DEAD_ZONE) return { dx, dy, mx: 0, my: 0 };
  const scaledMagnitude = Math.min(1, (magnitude - DEAD_ZONE) / (1 - DEAD_ZONE));
  return { dx, dy, mx: (nx / magnitude) * scaledMagnitude, my: (ny / magnitude) * scaledMagnitude };
}

/** Convert a two-finger distance change to camera-distance units. */
export function pinchZoomDelta(previousDistance: number, nextDistance: number): number {
  if (previousDistance <= 0 || nextDistance <= 0) return 0;
  return Math.max(-2, Math.min(2, (previousDistance - nextDistance) * 0.045));
}

export class TouchControls {
  readonly root: HTMLElement;
  private stickBase: HTMLElement;
  private stickKnob: HTMLElement;
  private moveZone: HTMLElement;
  private dashBtn: HTMLElement;
  private interactBtn: HTMLElement;
  private pauseBtn: HTMLElement;

  private stickPointerId: number | null = null;
  private stickOriginX = 0;
  private stickOriginY = 0;
  private mx = 0;
  private my = 0;
  private dashEdge = false;
  private pinchPoints = new Map<number, { x: number; y: number }>();
  private pinchDistance = 0;

  constructor(
    private ui: HTMLElement,
    private input: InputManager,
    private onPause: () => void,
    private onZoom: (delta: number) => void = () => {},
  ) {
    this.stickKnob = h('div', { class: 'touch-knob' });
    this.stickBase = h('div', { class: 'touch-stick' }, this.stickKnob);
    this.moveZone = h('div', { class: 'touch-move-zone', 'aria-label': 'Movement control' });
    this.dashBtn = h('button', { class: 'touch-dash', 'aria-label': 'Dash' }, 'DASH');
    this.interactBtn = h('button', { class: 'touch-interact', 'aria-label': 'Interact' }, 'USE');
    this.pauseBtn = h('button', { class: 'touch-pause', 'aria-label': 'Pause' }, '⏸');
    this.root = h('div', { class: 'touch-ui', id: 'touch-ui' }, this.moveZone, this.stickBase, this.dashBtn, this.interactBtn, this.pauseBtn);
    this.root.style.display = 'none';
    ui.appendChild(this.root);

    this.stickBase.style.display = 'none';

    // Dedicated left-side movement zone avoids accidental right-side ability presses.
    this.moveZone.addEventListener('pointerdown', (e) => this.onStickDown(e as PointerEvent));
    window.addEventListener('pointerdown', (e) => this.onPointerDown(e as PointerEvent));
    window.addEventListener('pointermove', (e) => this.onMove(e as PointerEvent));
    window.addEventListener('pointerup', (e) => this.onUp(e as PointerEvent));
    window.addEventListener('pointercancel', (e) => this.onUp(e as PointerEvent));

    this.dashBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.dashEdge = true;
    });
    this.interactBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.input.triggerAction();
    });
    this.pauseBtn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.onPause();
    });
  }

  private onPointerDown(e: PointerEvent): void {
    if (this.root.style.display === 'none' || e.pointerType !== 'touch') return;
    if (e.target instanceof Element && e.target.closest('button')) return;
    this.pinchPoints.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.updatePinch(false);
  }

  private onStickDown(e: PointerEvent): void {
    if (this.root.style.display === 'none') return;
    if (this.stickPointerId !== null) return;
    e.preventDefault();
    this.stickPointerId = e.pointerId;
    this.stickOriginX = e.clientX;
    this.stickOriginY = e.clientY;
    this.moveZone.setPointerCapture?.(e.pointerId);
    this.stickBase.style.display = 'block';
    // CSS centers the base on left/top via negative half-size margins.
    this.stickBase.style.left = `${e.clientX}px`;
    this.stickBase.style.top = `${e.clientY}px`;
    this.stickKnob.style.transform = 'translate(0, 0)';
  }

  private onMove(e: PointerEvent): void {
    if (this.pinchPoints.has(e.pointerId)) {
      this.pinchPoints.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.updatePinch(true);
    }
    if (e.pointerId !== this.stickPointerId) return;
    e.preventDefault();
    const { dx, dy, mx, my } = resolveTouchStick(e.clientX - this.stickOriginX, e.clientY - this.stickOriginY);
    this.stickKnob.style.transform = `translate(${dx}px, ${dy}px)`;
    this.mx = mx;
    this.my = my;
  }

  private onUp(e: PointerEvent): void {
    if (this.pinchPoints.delete(e.pointerId)) this.updatePinch(false);
    if (e.pointerId !== this.stickPointerId) return;
    if (this.moveZone.hasPointerCapture?.(e.pointerId)) this.moveZone.releasePointerCapture(e.pointerId);
    this.stickPointerId = null;
    this.stickBase.style.display = 'none';
    this.mx = 0;
    this.my = 0;
  }

  private updatePinch(emit: boolean): void {
    if (this.pinchPoints.size < 2) {
      this.pinchDistance = 0;
      return;
    }
    const [a, b] = [...this.pinchPoints.values()];
    const distance = Math.hypot(a.x - b.x, a.y - b.y);
    if (emit && this.pinchDistance > 0) {
      const delta = pinchZoomDelta(this.pinchDistance, distance);
      if (delta !== 0) this.onZoom(delta);
    }
    this.pinchDistance = distance;
  }

  /** Call every animation frame while mounted. */
  update(): void {
    if (this.root.style.display === 'none') return;
    this.input.setVirtual(this.mx, this.my, this.dashEdge);
    this.dashEdge = false;
  }

  setAbilityLabel(character: unknown): void {
    const def = characterDef(character);
    const name = def.ability.name;
    const short = def.id === 'barbarian' ? 'WAR CRY' : def.id === 'mage' ? 'NOVA' : def.id === 'ranger' ? 'VOLLEY' : def.id === 'rogue' ? 'PHASE' : 'DASH';
    this.dashBtn.textContent = short;
    this.dashBtn.setAttribute('aria-label', name);
  }

  setHouseMode(on: boolean): void {
    this.root.classList.toggle('house-mode', on);
  }

  setBuildMode(on: boolean, character: unknown = 'knight'): void {
    this.root.classList.toggle('build-mode', on);
    if (on) {
      this.dashBtn.textContent = 'ROTATE';
      this.dashBtn.setAttribute('aria-label', 'Rotate module 90 degrees');
      this.interactBtn.textContent = 'PLACE';
    } else {
      this.setAbilityLabel(character);
      this.interactBtn.textContent = 'USE';
    }
  }

  setVisible(v: boolean): void {
    this.root.style.display = v ? '' : 'none';
    if (!v) {
      this.stickPointerId = null;
      this.stickBase.style.display = 'none';
      this.mx = 0;
      this.my = 0;
      this.dashEdge = false;
      this.pinchPoints.clear();
      this.pinchDistance = 0;
      this.input.setVirtual(0, 0, false);
    }
  }
}
